import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getStatusForStage } from '../services/campaign.service';
import { campaignKeys } from '../services/campaign.keys';
import type { Campaign, CampaignKanbanStage } from '../types/campaign.types';

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campaignId, updates }: { campaignId: string; updates: Record<string, unknown> }) => {
      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', campaignId)
        .select()
        .single();

      if (error) {
        console.error('Error updating campaign:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.detail(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.withContact(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.budget(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: ['campaign-payments', variables.campaignId] });
      queryClient.invalidateQueries({ queryKey: campaignKeys.influencers(variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });

      toast({
        title: 'Success',
        description: 'Campaign updated successfully',
      });
    },
    onError: (error) => {
      console.error('Update campaign error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update campaign',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateKanbanStageAndStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      campaignId,
      kanban_stage,
    }: {
      campaignId: string;
      kanban_stage: CampaignKanbanStage;
    }) => {
      const newStatus = getStatusForStage(kanban_stage);

      const { error } = await supabase
        .from('campaigns')
        .update({
          kanban_stage: kanban_stage as any,
          status: newStatus as any,
        })
        .eq('id', campaignId);

      if (error) throw error;
      return { campaignId, kanban_stage, status: newStatus };
    },
    onMutate: async ({ campaignId, kanban_stage }) => {
      await queryClient.cancelQueries({ queryKey: campaignKeys.all });

      const previousCampaigns = queryClient.getQueryData<Campaign[]>(campaignKeys.all);

      if (previousCampaigns) {
        const newStatus = getStatusForStage(kanban_stage);
        queryClient.setQueryData<Campaign[]>(
          campaignKeys.all,
          previousCampaigns.map((c) =>
            c.id === campaignId
              ? {
                  ...c,
                  kanban_stage,
                  status: newStatus,
                }
              : c
          )
        );
      }

      return { previousCampaigns };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousCampaigns) {
        queryClient.setQueryData(campaignKeys.all, context.previousCampaigns);
      }
      toast({
        title: 'Failed to update',
        description: 'Could not update the campaign stage.',
        variant: 'destructive',
      });
    },
    onSuccess: ({ kanban_stage }) => {
      if (kanban_stage === 'completed') {
        toast({
          title: 'Campaign completed',
          description: 'Campaign status updated to completed.',
        });
      } else {
        toast({
          title: 'Stage updated',
          description: 'Campaign moved successfully.',
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
};

export const useCampaignMutations = () => {
  const updateCampaign = useUpdateCampaign();
  const updateStage = useUpdateKanbanStageAndStatus();

  return {
    updateCampaign,
    updateStage,
  };
};
